import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, 
  DollarSign, 
  Activity, 
  TrendingUp, 
  BarChart3, 
  Crown,
  ArrowLeft,
  RefreshCcw,
  Globe,
  Clock,
  Eye,
  UserCheck,
  MapPin,
  Monitor,
  Calendar,
  Zap,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Edit,
  Shield
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { logger } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminControlPanel = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [realtime, setRealtime] = useState(null);
  const [visits, setVisits] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDays, setSelectedDays] = useState(7);
  // Demo analytics — successful public-demo runs
  const [demoAnalytics, setDemoAnalytics] = useState(null);
  const [demoAnalyticsWindow, setDemoAnalyticsWindow] = useState('7d');
  
  // Edit User Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    email_verified: false,
    is_admin: false,
    plan: 'free',
    analyses_limit: 5,
    analyses_used: 0
  });

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
        navigate('/dashboard');
      }
    }
  }, [navigate]);

  const fetchRealtime = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/realtime`, { withCredentials: true });
      setRealtime(response.data);
    } catch (error) {
      logger.error('Realtime fetch error:', error);
    }
  }, []);

  const fetchVisits = useCallback(async (days = 7) => {
    try {
      const response = await axios.get(`${API}/admin/visits?days=${days}&limit=50`, { withCredentials: true });
      setVisits(response.data);
    } catch (error) {
      logger.error('Visits fetch error:', error);
    }
  }, []);

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      const skip = (page - 1) * 20;
      const response = await axios.get(`${API}/admin/users?skip=${skip}&limit=20`, { withCredentials: true });
      setUsers(response.data.users);
      setUsersTotal(response.data.total);
      setTotalPages(response.data.pages);
      setCurrentPage(page);
    } catch (error) {
      logger.error('Users fetch error:', error);
    }
  }, []);

  const fetchDemoAnalytics = useCallback(async (window) => {
    try {
      const response = await axios.get(
        `${API}/admin/demo-analytics?window=${window}`,
        { withCredentials: true }
      );
      setDemoAnalytics(response.data);
    } catch (error) {
      logger.error('Demo analytics fetch error:', error);
    }
  }, []);

  const refreshData = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchStats(),
      fetchRealtime(),
      fetchVisits(selectedDays),
      fetchUsers(currentPage),
      fetchDemoAnalytics(demoAnalyticsWindow),
    ]);
    setRefreshing(false);
    toast.success('Data refreshed');
  };

  const verifyUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/verify-user/${userId}`, {}, { withCredentials: true });
      toast.success('User verified successfully');
      fetchUsers(currentPage);
    } catch (error) {
      toast.error('Failed to verify user');
    }
  };

  const updateSubscription = async (userId, plan) => {
    try {
      await axios.post(`${API}/admin/update-subscription/${userId}?plan=${plan}`, {}, { withCredentials: true });
      toast.success(`Subscription updated to ${plan}`);
      fetchUsers(currentPage);
      fetchStats();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  // Open edit modal with user data
  const openEditModal = (userToEdit) => {
    const planLimits = { free: 5, student: 50, research: 200, lab: -1 };
    setEditingUser(userToEdit);
    setEditForm({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      email_verified: userToEdit.email_verified || false,
      is_admin: userToEdit.is_admin || false,
      plan: userToEdit.subscription?.plan || 'free',
      analyses_limit: userToEdit.subscription?.analyses_limit ?? planLimits[userToEdit.subscription?.plan] ?? 5,
      analyses_used: userToEdit.subscription?.analyses_used || 0
    });
    setEditModalOpen(true);
  };

  // Save user changes
  const saveUserChanges = async () => {
    if (!editingUser) return;
    
    try {
      // Update user details
      await axios.put(`${API}/admin/update-user/${editingUser.id}`, {
        name: editForm.name,
        email_verified: editForm.email_verified,
        is_admin: editForm.is_admin
      }, { withCredentials: true });

      // Update subscription
      await axios.post(`${API}/admin/update-subscription-full/${editingUser.id}`, {
        plan: editForm.plan,
        analyses_limit: parseInt(editForm.analyses_limit),
        analyses_used: parseInt(editForm.analyses_used)
      }, { withCredentials: true });

      toast.success('User updated successfully');
      setEditModalOpen(false);
      setEditingUser(null);
      fetchUsers(currentPage);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  // Reset usage counter
  const resetUsage = () => {
    setEditForm(prev => ({ ...prev, analyses_used: 0 }));
  };

  // Set unlimited analyses
  const setUnlimited = () => {
    setEditForm(prev => ({ ...prev, analyses_limit: -1 }));
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(), fetchRealtime(), fetchVisits(selectedDays), fetchUsers(1),
        fetchDemoAnalytics(demoAnalyticsWindow),
      ]);
      setLoading(false);
    };
    loadData();

    // Auto-refresh realtime stats every 30 seconds
    const interval = setInterval(fetchRealtime, 30000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchRealtime, fetchVisits, fetchUsers, fetchDemoAnalytics, selectedDays, demoAnalyticsWindow]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  const getCountryFlag = (countryCode) => {
    if (!countryCode || countryCode === 'XX' || countryCode === 'LO') return '🌐';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const getPlanColor = (plan) => {
    switch (plan) {
      case 'student': return 'bg-green-100 text-green-700';
      case 'research': return 'bg-blue-100 text-blue-700';
      case 'lab': return 'bg-purple-100 text-purple-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-400 mx-auto mb-4"></div>
          <p className="text-slate-300">Loading control panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white hover:bg-slate-700"
                data-testid="admin-back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg">
                  <Shield className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">Admin Control Panel</h1>
                  <p className="text-sm text-slate-400">Real-time Analytics & Management</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {realtime && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-green-500/20 border border-green-500/30 rounded-full">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-green-400 text-sm font-medium">{realtime.users_online} online</span>
                </div>
              )}
              <Button
                onClick={refreshData}
                disabled={refreshing}
                variant="outline"
                className="gap-2 border-slate-600 text-slate-300 hover:bg-slate-700"
                data-testid="refresh-data-button"
              >
                <RefreshCcw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div className="border-b border-slate-700 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex gap-1" data-testid="admin-tabs">
            {[
              { id: 'overview', label: 'Overview', icon: BarChart3 },
              { id: 'visitors', label: 'Visitors', icon: Globe },
              { id: 'users', label: 'Users', icon: Users },
              { id: 'revenue', label: 'Revenue', icon: DollarSign },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-cyan-400 text-cyan-400'
                    : 'border-transparent text-slate-400 hover:text-slate-200'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Real-time Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700" data-testid="stat-online">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Online Now</p>
                      <p className="text-2xl font-bold text-green-400">{realtime?.users_online || 0}</p>
                    </div>
                    <Zap className="w-8 h-8 text-green-400/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700" data-testid="stat-today-visits">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Today's Visits</p>
                      <p className="text-2xl font-bold text-cyan-400">{realtime?.today_visits || 0}</p>
                    </div>
                    <Eye className="w-8 h-8 text-cyan-400/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700" data-testid="stat-today-signups">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Today's Signups</p>
                      <p className="text-2xl font-bold text-purple-400">{realtime?.today_signups || 0}</p>
                    </div>
                    <UserCheck className="w-8 h-8 text-purple-400/50" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700" data-testid="stat-today-analyses">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Today's Analyses</p>
                      <p className="text-2xl font-bold text-amber-400">{realtime?.today_analyses || 0}</p>
                    </div>
                    <Activity className="w-8 h-8 text-amber-400/50" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="bg-slate-800/50 border-slate-700" data-testid="total-users-card">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-400">Total Users</CardTitle>
                  <Users className="w-5 h-5 text-blue-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats?.total_users || 0}</div>
                  <p className="text-xs text-slate-500 mt-1">
                    +{stats?.recent_signups_30d || 0} in last 30 days
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700" data-testid="total-revenue-card">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-400">Total Revenue</CardTitle>
                  <DollarSign className="w-5 h-5 text-green-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">
                    ${stats?.total_revenue?.toFixed(2) || '0.00'}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    MRR: ${stats?.mrr || 0}/month
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700" data-testid="active-subscriptions-card">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-400">Active Subscriptions</CardTitle>
                  <TrendingUp className="w-5 h-5 text-purple-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats?.active_subscriptions || 0}</div>
                  <p className="text-xs text-slate-500 mt-1">Paid subscribers</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700" data-testid="total-analyses-card">
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-slate-400">Total Analyses</CardTitle>
                  <Activity className="w-5 h-5 text-cyan-400" />
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-white">{stats?.total_analyses || 0}</div>
                  <p className="text-xs text-slate-500 mt-1">Reports generated</p>
                </CardContent>
              </Card>
            </div>

            {/* Plans Breakdown & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-slate-800/50 border-slate-700" data-testid="plans-breakdown-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <BarChart3 className="w-5 h-5 text-cyan-400" />
                    Subscription Plans
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-3">
                    {[
                      { name: 'Free', key: 'free', color: 'bg-slate-600' },
                      { name: 'Student', key: 'student', color: 'bg-green-500' },
                      { name: 'Research', key: 'research', color: 'bg-blue-500' },
                      { name: 'Lab', key: 'lab', color: 'bg-purple-500' },
                    ].map((plan) => (
                      <div key={plan.key} className="p-3 rounded-lg bg-slate-700/50">
                        <div className="flex items-center gap-2 mb-1">
                          <div className={`w-3 h-3 rounded-full ${plan.color}`}></div>
                          <span className="text-sm text-slate-300">{plan.name}</span>
                        </div>
                        <p className="text-2xl font-bold text-white">{stats?.plans_breakdown?.[plan.key] || 0}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/50 border-slate-700" data-testid="recent-activity-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Clock className="w-5 h-5 text-cyan-400" />
                    Recent Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {realtime?.recent_activity?.length > 0 ? (
                      realtime.recent_activity.map((activity, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 rounded bg-slate-700/30">
                          <span className="text-lg">{getCountryFlag(activity.country_code)}</span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{activity.path}</p>
                            <p className="text-xs text-slate-400">
                              {activity.city || activity.country || 'Unknown'} • {new Date(activity.timestamp).toLocaleTimeString()}
                            </p>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 text-center py-4">No recent activity</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ── Demo Analytics — successful public-demo runs ────────────── */}
            <Card
              className="bg-slate-800/50 border-slate-700 backdrop-blur"
              data-testid="demo-analytics-card"
            >
              <CardHeader>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-white flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-fuchsia-400" />
                      Demo Analytics
                    </CardTitle>
                    <CardDescription className="text-slate-400">
                      Which analyses public visitors run in the /demo tool. Privacy-safe — hashed IP + country only, auto-expires after 30 days.
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-1.5" data-testid="demo-analytics-window-selector">
                    {['24h', '7d', '30d', 'all'].map((w) => (
                      <button
                        key={w}
                        type="button"
                        onClick={() => setDemoAnalyticsWindow(w)}
                        data-testid={`demo-analytics-window-${w}`}
                        className={
                          demoAnalyticsWindow === w
                            ? 'px-3 py-1 rounded-md text-xs font-medium bg-fuchsia-600 text-white shadow'
                            : 'px-3 py-1 rounded-md text-xs font-medium bg-slate-700/60 text-slate-300 hover:bg-slate-700'
                        }
                      >
                        {w === 'all' ? 'All-time' : w}
                      </button>
                    ))}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!demoAnalytics ? (
                  <div className="text-sm text-slate-400 py-6 text-center">Loading demo analytics…</div>
                ) : demoAnalytics.total === 0 ? (
                  <div className="text-sm text-slate-400 py-6 text-center" data-testid="demo-analytics-empty">
                    No demo analyses in the selected window.
                  </div>
                ) : (
                  <>
                    <div className="flex items-baseline gap-3 mb-5">
                      <div
                        className="text-3xl font-bold text-white"
                        data-testid="demo-analytics-total"
                      >
                        {demoAnalytics.total.toLocaleString()}
                      </div>
                      <div className="text-sm text-slate-400">
                        successful analyses{' '}
                        <span className="text-slate-500">
                          ({demoAnalyticsWindow === 'all' ? 'all-time' : `last ${demoAnalyticsWindow}`})
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      {/* Ranking bar chart */}
                      <div className="h-72" data-testid="demo-analytics-ranking-chart">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                          Most-run analysis types
                        </div>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={demoAnalytics.by_type} layout="vertical" margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                            <XAxis type="number" tick={{ fontSize: 10, fill: '#94a3b8' }} allowDecimals={false} />
                            <YAxis
                              dataKey="label"
                              type="category"
                              tick={{ fontSize: 11, fill: '#cbd5e1' }}
                              width={70}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#1e293b',
                                border: '1px solid #334155',
                                borderRadius: '8px',
                                color: '#f1f5f9',
                              }}
                            />
                            <Bar dataKey="count" fill="#e879f9" radius={[0, 4, 4, 0]} name="Runs" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Recent events feed */}
                      <div data-testid="demo-analytics-recent">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                          Most recent runs
                        </div>
                        <div className="max-h-72 overflow-y-auto rounded-md border border-slate-700 bg-slate-900/40">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-800/80 sticky top-0">
                              <tr>
                                <th className="text-left py-2 px-3 text-xs font-medium text-slate-400">Type</th>
                                <th className="text-left py-2 px-3 text-xs font-medium text-slate-400">Country</th>
                                <th className="text-left py-2 px-3 text-xs font-medium text-slate-400">When</th>
                              </tr>
                            </thead>
                            <tbody>
                              {demoAnalytics.recent.length === 0 ? (
                                <tr>
                                  <td colSpan={3} className="py-4 px-3 text-slate-500 text-center">
                                    No recent events
                                  </td>
                                </tr>
                              ) : (
                                demoAnalytics.recent.map((ev) => (
                                  <tr
                                    key={`${ev.ts}-${ev.analysis_type}-${ev.country}`}
                                    className="border-b border-slate-800 last:border-b-0"
                                    data-testid="demo-analytics-recent-row"
                                  >
                                    <td className="py-2 px-3 font-medium text-white">{ev.label}</td>
                                    <td className="py-2 px-3 text-slate-300 font-mono text-xs">{ev.country}</td>
                                    <td className="py-2 px-3 text-slate-400 text-xs">
                                      {new Date(ev.ts).toLocaleString('en-US', {
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>

                    {demoAnalytics.by_country?.length > 0 && (
                      <div className="mt-6" data-testid="demo-analytics-countries">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                          Top countries
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {demoAnalytics.by_country.map((c) => (
                            <span
                              key={c.country}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-slate-700/50 text-xs"
                            >
                              <span className="font-mono text-slate-100">{c.country}</span>
                              <span className="text-slate-400">{c.count}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Visitors Tab */}
        {activeTab === 'visitors' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <Select value={selectedDays.toString()} onValueChange={(v) => { setSelectedDays(parseInt(v)); fetchVisits(parseInt(v)); }}>
                <SelectTrigger className="w-40 bg-slate-800 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-600">
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-sm text-slate-400">
                {visits?.total || 0} total visits • {visits?.unique_visitors || 0} unique visitors
              </div>
            </div>

            {/* Visitor Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Country Stats */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Globe className="w-5 h-5 text-cyan-400" />
                    Visitors by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {visits?.country_stats && Object.entries(visits.country_stats).length > 0 ? (
                      Object.entries(visits.country_stats).map(([country, count]) => (
                        <div key={country} className="flex items-center justify-between p-2 rounded bg-slate-700/30">
                          <span className="text-white">{country || 'Unknown'}</span>
                          <span className="text-cyan-400 font-medium">{count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 text-center py-4">No visitor data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Page Stats */}
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Monitor className="w-5 h-5 text-cyan-400" />
                    Top Pages
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {visits?.page_stats && Object.entries(visits.page_stats).length > 0 ? (
                      Object.entries(visits.page_stats).map(([page, count]) => (
                        <div key={page} className="flex items-center justify-between p-2 rounded bg-slate-700/30">
                          <span className="text-white truncate">{page}</span>
                          <span className="text-cyan-400 font-medium">{count}</span>
                        </div>
                      ))
                    ) : (
                      <p className="text-slate-400 text-center py-4">No page data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Visits Table */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-white">
                  <Eye className="w-5 h-5 text-cyan-400" />
                  Recent Visits
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="visits-table">
                    <thead>
                      <tr className="border-b border-slate-700">
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Location</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Page</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Time</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">IP</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visits?.visits?.length > 0 ? (
                        visits.visits.map((visit, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <span className="text-lg">{getCountryFlag(visit.country_code)}</span>
                                <div>
                                  <div className="text-white text-sm">{visit.country || 'Unknown'}</div>
                                  <div className="text-slate-400 text-xs">{visit.city || ''}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-white text-sm">{visit.path}</td>
                            <td className="py-3 px-4 text-slate-400 text-sm">{formatDate(visit.timestamp)}</td>
                            <td className="py-3 px-4 text-slate-500 text-xs font-mono">{visit.ip_address}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={4} className="py-8 text-center text-slate-400">No visits recorded yet</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Users className="w-5 h-5 text-cyan-400" />
                All Users ({usersTotal})
              </CardTitle>
              <CardDescription className="text-slate-400">Manage users and subscriptions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Plan</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Usage</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Joined</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-400">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                        <td className="py-3 px-4">
                          <div>
                            <div className="font-medium text-white flex items-center gap-2">
                              {u.name}
                              {u.is_admin && <Crown className="w-4 h-4 text-amber-400" />}
                            </div>
                            <div className="text-sm text-slate-400">{u.email}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {u.email_verified ? (
                            <span className="flex items-center gap-1 text-green-400 text-sm">
                              <CheckCircle className="w-4 h-4" /> Verified
                            </span>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => verifyUser(u.id)}
                              className="text-amber-400 border-amber-400/50 hover:bg-amber-400/10"
                            >
                              Verify
                            </Button>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <Select
                            value={u.subscription?.plan || 'free'}
                            onValueChange={(plan) => updateSubscription(u.id, plan)}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs ${getPlanColor(u.subscription?.plan)}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600">
                              <SelectItem value="free">Free</SelectItem>
                              <SelectItem value="student">Student</SelectItem>
                              <SelectItem value="research">Research</SelectItem>
                              <SelectItem value="lab">Lab</SelectItem>
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-300">
                          {u.subscription?.analyses_used || 0} / {u.subscription?.analyses_limit === -1 ? '∞' : (u.subscription?.analyses_limit || 2)}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-400">
                          {u.created_at ? new Date(u.created_at).toLocaleDateString() : 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-slate-400 hover:text-white hover:bg-slate-700"
                            onClick={() => openEditModal(u)}
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400">Page {currentPage} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchUsers(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="border-slate-600 text-slate-300"
                    >
                      <ChevronLeft className="w-4 h-4" /> Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchUsers(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="border-slate-600 text-slate-300"
                    >
                      Next <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Total Revenue</p>
                      <p className="text-3xl font-bold text-green-400">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
                    </div>
                    <DollarSign className="w-10 h-10 text-green-400/30" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Monthly Recurring</p>
                      <p className="text-3xl font-bold text-cyan-400">${stats?.mrr || 0}</p>
                    </div>
                    <TrendingUp className="w-10 h-10 text-cyan-400/30" />
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-slate-800/50 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">Paid Subscribers</p>
                      <p className="text-3xl font-bold text-purple-400">{stats?.active_subscriptions || 0}</p>
                    </div>
                    <Users className="w-10 h-10 text-purple-400/30" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Revenue by Plan */}
            <Card className="bg-slate-800/50 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white">Revenue by Plan</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { name: 'Student ($9/mo)', count: stats?.plans_breakdown?.student || 0, price: 9, color: 'bg-green-500' },
                    { name: 'Research ($29/mo)', count: stats?.plans_breakdown?.research || 0, price: 29, color: 'bg-blue-500' },
                    { name: 'Lab ($99/mo)', count: stats?.plans_breakdown?.lab || 0, price: 99, color: 'bg-purple-500' },
                  ].map((plan) => (
                    <div key={plan.name} className="flex items-center gap-4">
                      <div className={`w-3 h-3 rounded-full ${plan.color}`}></div>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-white">{plan.name}</span>
                          <span className="text-slate-400">{plan.count} subscribers</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${plan.color}`} 
                            style={{ width: `${Math.min((plan.count * plan.price) / (stats?.mrr || 1) * 100, 100)}%` }}
                          ></div>
                        </div>
                      </div>
                      <span className="text-green-400 font-medium w-24 text-right">
                        ${plan.count * plan.price}/mo
                      </span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Edit User Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-cyan-400" />
              Edit User
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Modify user details and subscription settings
            </DialogDescription>
          </DialogHeader>

          {editingUser && (
            <div className="space-y-4 py-4">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="edit-name" className="text-slate-300">Name</Label>
                <Input
                  id="edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>

              {/* Email (read-only) */}
              <div className="space-y-2">
                <Label className="text-slate-300">Email</Label>
                <Input
                  value={editForm.email}
                  disabled
                  className="bg-slate-700/50 border-slate-600 text-slate-400"
                />
              </div>

              {/* Switches Row */}
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    id="edit-verified"
                    checked={editForm.email_verified}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, email_verified: checked }))}
                  />
                  <Label htmlFor="edit-verified" className="text-slate-300">Verified</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="edit-admin"
                    checked={editForm.is_admin}
                    onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_admin: checked }))}
                  />
                  <Label htmlFor="edit-admin" className="text-slate-300 flex items-center gap-1">
                    <Crown className="w-4 h-4 text-amber-400" /> Admin
                  </Label>
                </div>
              </div>

              {/* Subscription Plan */}
              <div className="space-y-2">
                <Label className="text-slate-300">Subscription Plan</Label>
                <Select
                  value={editForm.plan}
                  onValueChange={(plan) => {
                    const planLimits = { free: 5, student: 50, research: 200, lab: -1 };
                    setEditForm(prev => ({ 
                      ...prev, 
                      plan, 
                      analyses_limit: planLimits[plan]
                    }));
                  }}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-800 border-slate-600">
                    <SelectItem value="free">Free (5 analyses/mo)</SelectItem>
                    <SelectItem value="student">Student (50 analyses/mo)</SelectItem>
                    <SelectItem value="research">Research (200 analyses/mo)</SelectItem>
                    <SelectItem value="lab">Lab (Unlimited)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Analyses Limit & Used */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-limit" className="text-slate-300">Analyses Limit</Label>
                  <div className="flex gap-2">
                    <Input
                      id="edit-limit"
                      type="number"
                      value={editForm.analyses_limit}
                      onChange={(e) => setEditForm(prev => ({ ...prev, analyses_limit: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={setUnlimited}
                      className="border-purple-500 text-purple-400 hover:bg-purple-500/20 text-xs"
                    >
                      ∞
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500">-1 = unlimited</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-used" className="text-slate-300">Analyses Used</Label>
                  <div className="flex gap-2">
                    <Input
                      id="edit-used"
                      type="number"
                      value={editForm.analyses_used}
                      onChange={(e) => setEditForm(prev => ({ ...prev, analyses_used: e.target.value }))}
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={resetUsage}
                      className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
                    >
                      <RefreshCcw className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveUserChanges}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminControlPanel;
